import { z } from 'zod';

export const zAppInitParams = z.object({
  drToken: z.string(),
  redirectUrl: z.string().url().nullable(),
  languageCode: z.string().optional(),
  allowOrderFiltering: z.boolean(),
  allowOrderRowFiltering: z.boolean(),
  loginType: z.string(),
  email: z.string().email().nullable(),
  customerNumber: z.string().nullable(),
  initOrderKeys: z.string().array().optional(),
  initOrderLines: z.string().optional().array().optional(),
  returnType: z.string().optional().nullable(),
})
.strip();

export const zAppInit = z.object({
  params: zAppInitParams,
  updateDefaultParams: z.function().args(zAppInitParams).returns(z.void()),
});

export const zImage = z.object({
  salesOrderLineId: z.number(),
  dataURL: z.string(),
  file: z.instanceof(File),
  reference: z.string().optional(),
});

export const zReturnData = z.object({
  amount: z.number(),
  reason: z.string(),
  readableReason: z.string(),
  returnDescription: z.string(),
  images: zImage.array().optional(),
});

// The format needed for a specific orderLine
export const zOrderLine = z.object({
  salesOrderLineId: z.number(),
  lineNumber: z.string().nullable().optional(),
  quantity: z.number(),
  shippedQuantity: z.number().nullable().optional(),
  mayBeReturned: z.boolean(),
  labelTranslationKey: z.string(),
  // product
  productNumber: z.string().nullable(),

  description: z.string().nullable(),
  variant: z.string().nullable(),
  variantDescription: z.string().nullable(),
  imageUrl: z.string().nullable(),
  // custom
  uid: z.string(),
  orderNumber: z.string(),
  orderDate: z.string().nullable().optional(),
});

export const zOrder = z.object({
  customerNumber: z.string(),
  orderNumber: z.string(),
  orderDate: z.string(),
  numberOfShippedOrderLines: z.number().nullable().optional(),
  orderNumberAliases: z.string().array().nullable(),
  orderLines: zOrderLine.array(),
});

export const zUpdateAndAddToReturnArgs = zOrderLine.extend({
  returnData: zReturnData,
  initialReturnReason: z.string().nullable().optional(),
});

export const zReturnForm = zOrderLine.extend({
  layout: z.enum(['orderLine', 'returnLine']),
  setOpen: z.function().args(z.boolean()).returns(z.void()),
  returnData: zReturnData.optional(),
});

export const zCarrierItem = z.object({
  carrier: z.string(),
  logoUrl: z.string(),
  service: z.string(),
  code: z.string(),
});

export const zCarrierSelected = z.object({
  service: z.string(),
  code: z.string(),
  logoUrl: z.string(),
  carrier: z.string(),
  numberOfParcels: z.number().optional(),
});

export const zSummaryOrderLines = z.object({
  salesOrderLineId: z.number(),
  returnedQuantity: z.number(),
  comment: z.string(),
  reasonCodes: z.object({
    id: z.number(),
    rrmId: z.number(),
  }),
  imageReferences: z.string().array().nullable(),
  exchange: z.object({
    productNumber: z.string(),
    variant: z.string(),
  }).nullable(),
});

export const zSummaryItem = z.object({
  orderNumber: z.string(),
  returnFulfilmentType: z.string(),
  returnOrderLines: zSummaryOrderLines.array(),
});

export const zSummaryPostObject = z.object({
  returnOrders: zSummaryItem.array(),
  chosenCarrierService: z.string(),
});

export const zReturnContactInfo = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phoneNumber: z.string(),
});

export const zReturnOrderLines = z.object({
  salesOrderLineId: z.number(),
  returnedQuantity: z.number(),
  reasonCode: z.object({
    id: z.number(),
    rrmId: z.number(),
  }),
  comment: z.string(),
  imageReferences: z.string().array(),
  exchange: z.object({
    productNumber: z.string(),
    variant: z.string(),
  }),
});

export const zReturnItem = z.object({
  salesOrderNumber: z.string(),
  returnOrderNumber: z.string(),
  returnSource: z.string(),
  returnOrderDate: z.string(),
  returnFulfilmentType: z.string(),
  returnOrderLines: zReturnOrderLines.array(),
});

export const zReturnPostObject = z.object({
  customerNumber: z.string(),
  iso6391: z.string(),
  returnOrders: zReturnItem.array(),
  chosenCarrierService: z.string(),
  numberOfParcels: z.number(),
  decisions: z.unknown().optional(),
  summaryResult: z.object({
    adjustedForShipment: z.boolean(),
    policyApplied: z.boolean(),
    decisionsApplied: z.boolean(),
  }),
  contact: zReturnContactInfo,
});

export const zOption = z.object({
  value: z.string(),
  label: z.string(),
});
